import {
  EntityLinkCommandSchema,
  EntityLinkProjectionSchema,
  GraphResultSchema,
  GraphRootSearchCommandSchema,
  GraphRootSearchResultSchema,
  GraphTraversalQuerySchema,
  UuidSchema,
  type EntityLinkCommand,
  type EntityLinkProjection,
  type GraphEdgeType,
  type GraphNodeType,
  type GraphResult,
  type GraphRootSearchResult,
  type GraphRootReference,
} from "@mind/contracts/browser";
import { apiRequest, type ApiRequestOptions } from "../../api/client.js";

export const GRAPH_NODE_TYPES: readonly GraphNodeType[] = [
  "note",
  "project",
  "task",
  "document",
  "focus_thread",
  "research_source",
  "account",
];

export const GRAPH_EDGE_TYPES: readonly GraphEdgeType[] = [
  "references",
  "related_to",
  "derived_from",
  "supports",
  "assigned_to",
];

export const GRAPH_MUTABLE_EDGE_TYPES: readonly Exclude<GraphEdgeType, "assigned_to">[] = [
  "references",
  "related_to",
  "derived_from",
  "supports",
];

export type GraphTraversalInput = {
  readonly workspaceId: string;
  readonly root: GraphRootReference;
  readonly projectId?: string;
  readonly accountId?: string;
  readonly tag?: string;
  readonly includeArchived: boolean;
  readonly cursor?: string | null;
  readonly limit?: number;
};
export type CreateEntityLinkCommand = Extract<EntityLinkCommand, { readonly action: "create" }>;
export type RemoveEntityLinkCommand = Extract<EntityLinkCommand, { readonly action: "remove" }>;


function pathWithQuery(
  path: string,
  parameters: Readonly<Record<string, string | number | boolean | null | undefined>>,
): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(parameters)) {
    if (value !== null && value !== undefined && value !== "") query.set(key, String(value));
  }
  const encoded = query.toString();
  return encoded.length === 0 ? path : `${path}?${encoded}`;
}

function withOptionalSignal(options: ApiRequestOptions, signal?: AbortSignal): ApiRequestOptions {
  return signal === undefined ? options : { ...options, signal };
}

export const graphApi = {
  searchRoots(workspaceId: string, query: string, cursor: string | null, signal?: AbortSignal): Promise<GraphRootSearchResult> {
    const body = GraphRootSearchCommandSchema.parse({
      workspaceId: UuidSchema.parse(workspaceId),
      query,
      nodeTypes: GRAPH_NODE_TYPES,
      cursor,
      limit: 50,
    });
    return apiRequest(
      GraphRootSearchResultSchema,
      "/api/graph/roots/search",
      withOptionalSignal({ method: "POST", body }, signal),
    );
  },

  traverse(input: GraphTraversalInput, signal?: AbortSignal): Promise<GraphResult> {
    const query = GraphTraversalQuerySchema.parse({
      workspaceId: input.workspaceId,
      root: input.root,
      nodeTypes: GRAPH_NODE_TYPES,
      edgeTypes: GRAPH_EDGE_TYPES,
      projectId: input.projectId ?? null,
      accountId: input.accountId ?? null,
      tag: input.tag ?? null,
      includeArchived: input.includeArchived,
      cursor: input.cursor ?? null,
      limit: input.limit ?? 50,
    });
    return apiRequest(
      GraphResultSchema,
      pathWithQuery("/api/graph", {
        workspaceId: query.workspaceId,
        rootType: query.root.type,
        rootId: query.root.id,
        rootRevision: query.root.revision,
        nodeTypes: query.nodeTypes.join(","),
        edgeTypes: query.edgeTypes.join(","),
        projectId: query.projectId,
        accountId: query.accountId,
        tag: query.tag,
        includeArchived: query.includeArchived,
        cursor: query.cursor,
        limit: query.limit,
      }),
      withOptionalSignal({}, signal),
    );
  },

  createLink(input: CreateEntityLinkCommand): Promise<EntityLinkProjection> {
    return apiRequest(EntityLinkProjectionSchema, "/api/entity-links", {
      method: "POST",
      body: EntityLinkCommandSchema.parse(input),
    });
  },

  removeLink(input: RemoveEntityLinkCommand): Promise<EntityLinkProjection> {
    return apiRequest(EntityLinkProjectionSchema, "/api/entity-links", {
      method: "DELETE",
      body: EntityLinkCommandSchema.parse(input),
    });
  },
};

export function createEntityLinkCommand(input: {
  readonly commandId: string;
  readonly linkId: string;
  readonly workspaceId: string;
  readonly linkType: Exclude<GraphEdgeType, "assigned_to">;
  readonly source: GraphRootReference;
  readonly target: GraphRootReference;
  readonly sourceMessageId?: string | null;
}): CreateEntityLinkCommand {
  return EntityLinkCommandSchema.parse({
    action: "create",
    commandId: input.commandId,
    linkId: input.linkId,
    workspaceId: input.workspaceId,
    linkType: input.linkType,
    source: input.source,
    target: input.target,
    sourceMessageId: input.sourceMessageId ?? null,
  }) as CreateEntityLinkCommand;
}

export function createEntityUnlinkCommand(input: {
  readonly commandId: string;
  readonly linkId: string;
  readonly workspaceId: string;
  readonly revision: string;
}): RemoveEntityLinkCommand {
  return EntityLinkCommandSchema.parse({ action: "remove", ...input }) as RemoveEntityLinkCommand;
}
